import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lightning } from "@phosphor-icons/react";

/**
 * Handles the `#session_id=<uuid>` fragment returned by Emergent OAuth.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Flow:
 *   1. Read `session_id` from location.hash.
 *   2. POST it to /api/auth/google/session (login) or /api/auth/google/link (linking).
 *   3. Clear the hash, refresh the auth context, navigate to the right page.
 *
 * The `intent=link` query param (added when the user clicks "Lier" in Réglages)
 * switches the mode to linking — the backend then attaches the Google identity
 * to the currently authenticated user instead of issuing a new session.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Guard against double-firing under StrictMode / rerender
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Emergent OAuth normally puts session_id in the URL fragment, but some
    // browsers (iOS Safari standalone / PWA) can strip fragments. Fall back
    // to query params so the flow survives edge cases.
    const hashRaw = location.hash || "";
    const hashParams = new URLSearchParams(hashRaw.startsWith("#") ? hashRaw.slice(1) : hashRaw);
    const queryParams = new URLSearchParams(location.search);
    const sessionId = hashParams.get("session_id") || queryParams.get("session_id");
    const intent = queryParams.get("intent"); // "link" or null (default = login)

    // Console breadcrumbs — surface *what* the callback saw so support can debug.
    console.info("[AuthCallback] url=", window.location.href, "sessionId=", sessionId ? sessionId.slice(0, 8) + "…" : null);

    if (!sessionId) {
      toast.error("Session Google manquante — recommence depuis l'écran de connexion.");
      navigate("/login", { replace: true });
      return;
    }

    const run = async () => {
      try {
        if (intent === "link") {
          const { data } = await api.post("/auth/google/link", null, {
            headers: { "X-Session-ID": sessionId },
            timeout: 20000,
          });
          toast.success(`Compte Google lié : ${data.email}`);
          window.history.replaceState({}, document.title, "/reglages");
          await refresh();
          navigate("/reglages", { replace: true });
        } else {
          const { data } = await api.post("/auth/google/session", null, {
            headers: { "X-Session-ID": sessionId },
            timeout: 20000,
          });
          toast.success(`Bienvenue ${data.user?.name || ""}`);
          window.history.replaceState({}, document.title, "/");
          await refresh();
          navigate("/", { replace: true, state: { user: data.user } });
        }
      } catch (err) {
        console.error("[AuthCallback] failed", err?.response?.status, err?.response?.data, err?.message);
        // Distinguish network errors from API errors so the user knows if
        // it's a whitelist issue vs a genuine connectivity problem.
        let msg;
        if (err?.response?.data?.detail) {
          msg = err.response.data.detail;
        } else if (err?.code === "ECONNABORTED") {
          msg = "Délai d'attente dépassé — réessaie dans quelques secondes.";
        } else if (err?.message === "Network Error") {
          msg = "Impossible de joindre le serveur BKflow. Vérifie ta connexion puis recommence.";
        } else {
          msg = formatApiError(err) || "Connexion Google impossible";
        }
        toast.error(msg);
        window.history.replaceState({}, document.title, "/login");
        navigate("/login", { replace: true });
      }
    };
    run();
  }, [location, navigate, refresh]);

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4" data-testid="auth-callback">
      <div className="w-14 h-14 rounded-2xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_30px_rgba(0,92,255,0.5)]">
        <Lightning size={28} weight="fill" color="white" />
      </div>
      <div className="text-sm text-white/70">Finalisation de la connexion…</div>
      <div className="w-8 h-8 border-2 border-white/20 border-t-[#005CFF] rounded-full animate-spin" />
    </div>
  );
}
